import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { searchByName, type OffProduct } from "@/api/openfoodfacts";

/**
 * Food search with the spec's "cache first, then OFF" pattern (SPEC 2.2.1).
 *
 * 1. Debounce input by 250ms.
 * 2. Query the user's `food` table via pg_trgm fuzzy match (already indexed
 *    in the initial migration: `food_user_name_trgm_idx`).
 * 3. If fewer than 5 cache hits, fan out to Open Food Facts.
 * 4. Merge: cache hits first, then OFF candidates de-duped by source_id.
 *
 * Result rows expose a uniform shape regardless of source.
 */

export type FoodSearchHit =
  | {
      kind: "cache";
      id: string;                    // food.id (uuid)
      name: string;
      brand: string | null;
      kcalPer100: number;
      basis: string;
    }
  | {
      kind: "pantry";
      // BW12.5 Issue 15 — Pantry items shown first in meal-log search so the
      // user finds what they actually have on hand before falling out to OFF.
      pantryItemId: string;
      foodId: string | null;
      id: string; // food.id (or pantry-item id when food link is missing)
      name: string;
      brand: string | null;
      kcalPer100: number;
      basis: string;
      qtyRemaining: number | null;
      unit: string;
      expiresAt: string | null;
    }
  | {
      kind: "off";
      // No food.id yet — this hit needs a "promote to food" step before it
      // can be referenced from a meal. Source is OFF's barcode (EAN).
      offCode: string;
      name: string;
      brand: string | null;
      kcalPer100: number;
      product: OffProduct;
    };

export function useFoodSearch(query: string): {
  hits: FoodSearchHit[];
  isLoading: boolean;
  error: Error | null;
} {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(t);
  }, [query]);

  const q = useQuery<FoodSearchHit[]>({
    queryKey: ["food_search", userId, debounced],
    enabled: Boolean(userId) && debounced.length >= 2,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      if (!userId || debounced.length < 2) return [];

      // 1) Pantry first — what the user actually has on hand. Match the
      //    pantry item's linked food name (and brand) via ilike. We pull
      //    macros from the linked food row so the meal log can pre-fill.
      const { data: pantryRows, error: pantryErr } = await supabase
        .from("pantry_item")
        .select(
          "id, qty, unit, expires, food_id, food:food!inner(id, name, brand, kcal, basis)",
        )
        .eq("user_id", userId)
        .ilike("food.name", `%${debounced}%`)
        .gt("qty", 0)
        .limit(6);
      if (pantryErr) throw pantryErr;
      const pantryHits: FoodSearchHit[] = (pantryRows ?? []).map((r) => {
        const f = (r as unknown as { food: { id: string; name: string; brand: string | null; kcal: number | null; basis: string } }).food;
        return {
          kind: "pantry",
          pantryItemId: (r as { id: string }).id,
          foodId: f?.id ?? null,
          id: f?.id ?? (r as { id: string }).id,
          name: f?.name ?? "(pantry item)",
          brand: f?.brand ?? null,
          kcalPer100: Number(f?.kcal ?? 0),
          basis: f?.basis ?? "per_100g",
          qtyRemaining: Number((r as { qty?: number }).qty ?? 0),
          unit: String((r as { unit?: string }).unit ?? "g"),
          expiresAt: ((r as { expires?: string | null }).expires ?? null) as string | null,
        };
      });

      // 2) Existing food cache (pg_trgm)
      const { data: cacheRows, error: cacheErr } = await supabase
        .from("food")
        .select("id, name, brand, kcal, basis")
        .eq("user_id", userId)
        .ilike("name", `%${debounced}%`)
        .order("is_favorite", { ascending: false })
        .order("name", { ascending: true })
        .limit(8);
      if (cacheErr) throw cacheErr;
      // Skip foods that already surfaced as a pantry hit so we don't
      // double-render the same product.
      const pantryFoodIds = new Set(
        pantryHits
          .map((h) => (h.kind === "pantry" ? h.foodId : null))
          .filter(Boolean) as string[],
      );
      const cache: FoodSearchHit[] = (cacheRows ?? [])
        .filter((r) => !pantryFoodIds.has(r.id))
        .map((r) => ({
          kind: "cache",
          id: r.id,
          name: r.name,
          brand: r.brand,
          kcalPer100: Number(r.kcal ?? 0),
          basis: r.basis,
        }));

      const before = [...pantryHits, ...cache];
      if (before.length >= 8) return before.slice(0, 12);

      // 3) Open Food Facts fallback. HOTFIX-BW18.1 #5:
      //    - Bias to Switzerland so Coop / Migros / Denner products rank ahead
      //      of the long international tail.
      //    - Post-filter: every query token must appear in product_name OR
      //      brand. OFF's tag/category-based ranking surfaced unrelated hits
      //      (e.g. "vegan protein" → cashew nuts) before this gate.
      try {
        const off = await searchByName(debounced, {
          pageSize: 16,
          country: "ch",
          signal,
        });
        const tokens = debounced
          .toLowerCase()
          .split(/\s+/)
          .filter((t) => t.length >= 2);
        const seenCodes = new Set<string>();
        const offHits: FoodSearchHit[] = [];
        for (const p of off.products) {
          if (!p.code || seenCodes.has(p.code)) continue;
          if (!tokensMatch(p, tokens)) continue;
          seenCodes.add(p.code);
          offHits.push({
            kind: "off",
            offCode: p.code,
            name: p.product_name?.trim() || `OFF ${p.code}`,
            brand: p.brands?.split(",")[0]?.trim() || null,
            kcalPer100: Number(p.nutriments?.["energy-kcal_100g"] ?? 0),
            product: p,
          });
        }
        return [...before, ...offHits].slice(0, 14);
      } catch (e) {
        // OFF outage shouldn't kill cache or pantry hits.
        if (e instanceof DOMException && e.name === "AbortError") {
          return before;
        }
        return before;
      }
    },
  });

  return {
    hits: q.data ?? [],
    isLoading: q.isLoading || q.isFetching,
    error: q.error as Error | null,
  };
}

function tokensMatch(p: OffProduct, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const haystack = `${p.product_name ?? ""} ${p.brands ?? ""}`.toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}
